import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import { api, errMsg, cleanPhone } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader } from "@/pages/Classes";
import { Lock, Sparkles, Percent, CalendarOff, Clock, MapPin, User } from "lucide-react";
import { fmtShort, fmtTime, fmtRange } from "@/lib/enrol";
import EmailOptInCheckbox, { RequiredMark } from "@/components/EmailOptInCheckbox";
import PhotoConsentCheckbox from "@/components/PhotoConsentCheckbox";
import { PromoCodeInput } from "@/components/PromoCodeInput";

const REQUIRED_FIELDS = ["full_name", "email", "phone", "age"];
const FIELD_LABELS = {
  full_name: "Full name",
  parent_name: "Parent / guardian name",
  email: "Email",
  phone: "Phone",
  age: "Age",
};
const FIELD_TYPES = { email: "email", phone: "tel", age: "number" };

export default function Register() {
  const { sessionId } = useParams();
  const { settings } = useOutletContext() || {};
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [form, setForm] = useState({ full_name: sp.get("name") || "", email: sp.get("email") || "", phone: sp.get("phone") || "", age: sp.get("age") || "", parent_name: sp.get("parent") || "", notes: "", email_opt_in: false, photo_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  const isReenrol = sp.get("rf") === "1";
  const refCode = (sp.get("ref") || localStorage.getItem("ref_code") || "").toUpperCase();
  useEffect(() => { if (sp.get("ref")) localStorage.setItem("ref_code", sp.get("ref").toUpperCase()); }, [sp]);

  // Re-fetch when the session changes; `api` is a module singleton, `s`/`r`/`err` are callback locals.
  useEffect(() => {
    api.get("/sessions").then(r => setSession(r.data.find(s => s.id === sessionId)));
    api.get(`/pricing/class-preview/${sessionId}`).then(r => setPricing(r.data)).catch(err => console.warn("Pricing preview unavailable:", err?.message));
  }, [sessionId]);

  // "Finish your booking" link from the abandoned-checkout email: prefill + jump straight back into checkout
  const resumeId = sp.get("resume"), resumeTok = sp.get("t");
  const [resume, setResume] = useState(null);
  useEffect(() => {
    if (!resumeId || !resumeTok) return;
    api.get(`/checkout/resume/class/${resumeId}`, { params: { t: resumeTok } }).then(r => {
      if (r.data.already_paid) { toast.success("Good news — this booking is already paid."); return; }
      setForm(f => ({ ...f, ...r.data.form, age: r.data.form.age ?? f.age }));
      setResume(r.data);
    }).catch(() => toast.error("That link has expired — please fill in your details below."));
  }, [resumeId, resumeTok]);
  useEffect(() => {
    if (resume && session && !submitting) { toast("Resuming your booking…"); submit(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, session]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/class-checkout", {
        session_id: sessionId, ...form,
        age: form.age ? parseInt(form.age) : null,
        referred_by_code: refCode || null,
        promo_code: promo?.code || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Failed to start checkout"));
      setSubmitting(false);
    }
  };

  const missingRequired = REQUIRED_FIELDS.some(k => !String(form[k] || "").trim());

  if (!session) return <div className="pt-40 text-center text-zinc-500">Loading…</div>;

  const basePrice = pricing?.prorata_applied ? pricing.adjusted_price : (pricing?.original_price ?? session.price);
  const displayPrice = promo ? promo.final : basePrice;
  const prorata = pricing?.prorata_applied;

  return (
    <>
      <PageHeader eyebrow={isReenrol ? "Welcome back" : "Register"} title={isReenrol ? "Confirm your next term" : "Confirm your spot"} back />
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-2 gap-8">
        {isReenrol && (
          <div className="md:col-span-2 -mb-4 flex items-center gap-2 text-sm text-red-400 border border-red-600/30 bg-red-600/10 px-4 py-3" data-testid="reenrol-banner">
            <Sparkles size={14}/> One-click re-enrol · your details are pre-filled. Just review and pay.
          </div>
        )}
        {refCode && !isReenrol && settings?.referral_program_enabled && (
          <div className="md:col-span-2 -mb-4 flex items-center gap-2 text-sm text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="referral-banner">
            <Sparkles size={14}/> Referral applied · code <strong className="font-mono ml-1">{refCode}</strong>. Both you and your friend get a free class after payment.
          </div>
        )}
        {prorata && (
          <div className="md:col-span-2 -mb-4 flex items-start gap-2 text-sm text-amber-200 border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="prorata-banner">
            <Percent size={14} className="mt-0.5 shrink-0"/>
            <div>
              <strong>Pro-rata pricing applied</strong> — you're joining mid-term.
              You pay only for the <strong>{pricing.remaining_classes} of {pricing.total_classes}</strong> classes remaining ·
              <span className="ml-1">${pricing.price_per_class.toFixed(2)} × {pricing.remaining_classes} = <strong>${pricing.adjusted_price.toFixed(2)}</strong></span>
              <span className="line-through text-zinc-500 ml-2">${pricing.original_price.toFixed(2)}</span>
            </div>
          </div>
        )}
        <div className="card-surface p-8">
          <div className="eyebrow mb-4">Session</div>
          <div className="font-display text-3xl uppercase">{session.batch_name}</div>
          {session.age_range && <div className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-red-600/60 text-red-400" data-testid="session-age-range">Ages {session.age_range}</div>}
          <div className="mt-4 text-zinc-300 space-y-2 text-sm">
            <div className="flex items-center gap-2"><Clock size={14} className="text-red-500 shrink-0"/>{session.day} · {fmtRange(session.start_time, session.end_time)}</div>
            <div className="flex items-start gap-2"><MapPin size={14} className="text-red-500 shrink-0 mt-0.5"/><span>{session.location_name}{session.location_address && <span className="block text-zinc-500 text-xs">{session.location_address}</span>}</span></div>
            {session.instructor && <div className="flex items-center gap-2"><User size={14} className="text-red-500 shrink-0"/>Instructor: {session.instructor}</div>}
            <div>
              {pricing?.total_classes || session.classes_per_term} classes per term
              {prorata && <span className="text-amber-300 ml-2" data-testid="remaining-classes">· {pricing.remaining_classes} remaining</span>}
            </div>
            {session.schedule?.first_class && <div data-testid="term-dates-line">{session.schedule.term_name ? `${session.schedule.term_name} · ` : ""}first class {fmtShort(session.schedule.first_class)} · last class {fmtShort(session.schedule.last_class)}</div>}
          </div>
          {session.schedule?.skip_dates?.length > 0 && (
            <div className="mt-4 border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs px-4 py-3 flex items-start gap-2" data-testid="holiday-notice">
              <CalendarOff size={14} className="shrink-0 mt-0.5"/>
              <span>Ends {session.schedule.skip_dates.length === 1 ? "a week" : `${session.schedule.skip_dates.length} weeks`} later — no class {session.schedule.skip_dates.map(fmtShort).join(", ")}. You still get all {session.schedule.classes_count} classes.</span>
            </div>
          )}
          <div className="border-t border-white/10 mt-6 pt-6 flex items-baseline justify-between">
            <div>
              <div className="text-3xl font-display" data-testid="display-price">{Number(basePrice) > 0 ? `$${Number(displayPrice).toFixed(2)}` : "Price TBA"}</div>
              {prorata && <div className="text-xs text-zinc-500 line-through mt-1">${pricing.original_price.toFixed(2)} full term</div>}
              {promo && <div className="text-xs text-emerald-400 mt-1" data-testid="promo-saving">Promo {promo.code} · −${Number(promo.discount).toFixed(2)}</div>}
            </div>
            <div className="text-xs text-zinc-500">AUD · one-off</div>
          </div>
        </div>
        <div className="card-surface p-8">
          <div className="eyebrow mb-4">Your details</div>
          {["full_name","parent_name","email","phone","age"].map(k => {
            const req = REQUIRED_FIELDS.includes(k);
            return (
              <div key={k} className="mb-3">
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  {FIELD_LABELS[k]}{req && <RequiredMark/>}
                </label>
                <input
                  type={FIELD_TYPES[k] || "text"}
                  required={req}
                  aria-required={req}
                  placeholder={FIELD_LABELS[k]}
                  value={form[k]}
                  onChange={e => setForm({...form,[k]: k === "phone" ? cleanPhone(e.target.value) : e.target.value})}
                  className={`w-full bg-black border px-4 py-3 focus:border-red-600 outline-none ${req && !String(form[k] || "").trim() ? "border-white/10" : "border-white/10"}`}
                  data-testid={`reg-${k}`}
                />
              </div>
            );
          })}
          <textarea placeholder="Notes (allergies, medical, etc.)" value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} className="w-full bg-black border border-white/10 px-4 py-3 mb-4 focus:border-red-600 outline-none" rows={3} />
          <EmailOptInCheckbox checked={form.email_opt_in} onChange={v => setForm({...form, email_opt_in: v})} testId="reg-email-opt-in"/>
          <PhotoConsentCheckbox checked={form.photo_consent} onChange={v => setForm({...form, photo_consent: v})} testId="reg-photo-consent"/>
          <PromoCodeInput type="class" amount={basePrice} batchId={session.batch_id} onApplied={setPromo} testId="reg-promo"/>
          {Number(basePrice) <= 0 && <div className="text-amber-300 text-xs mb-3" data-testid="no-price-notice">Pricing for this term hasn't been published yet — please check back soon or contact us.</div>}
          <button onClick={submit} disabled={submitting || missingRequired || Number(basePrice) <= 0} className="btn-red w-full disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-registration">
            <Lock size={14}/>{submitting ? "Redirecting…" : Number(basePrice) > 0 ? `Pay $${Number(displayPrice).toFixed(2)} securely` : "Price not set yet"}
          </button>
          <p className="text-xs text-zinc-500 mt-4 flex items-center gap-2"><Lock size={12}/>Secure Stripe checkout</p>
        </div>
      </div>
    </>
  );
}
